import { useAuthActions, useLoggedInMember, useLoggedInOrganization } from "@/modules/auth/states";
import DoubleArrowIcon from "@/shared/icons/double-left";
import ZaniaIcon from "@/shared/icons/zania";
import ZaniaExpandedIcon from "@/shared/icons/zania-expanded";
import { cn } from "@/shared/utils/classname-merger";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@nextui-org/react";
import { motion } from "framer-motion";
import { Ellipsis } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mainSidebarItems } from "../../constants/sidebar";
import NavigationMenu from "./menu-item";

import { shouldEnableInsights } from "@/main";
import LogoutIcon from "@/shared/icons/logout";
import OrganizationIcon from "@/shared/icons/organization";
import Bugsnag from "@bugsnag/js";
import {
  setLoginStep,
  useDiscoveredOrganizations,
  useLoginActions
} from "../../../auth/states/login";

import { authChannel } from "@/infra/auth/broadcast";
import { getOrganizationList, revokeToken } from "@/modules/auth/requests";
import { LOGIN_STEP } from "@/modules/auth/types";
import ZAvatarGradient from "@/shared/components/avatarGradient";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}
interface IdentifyArgs {
  userId: string;
  email: string;
  name: string;
}

interface MenuItem {
  title: string;
  icon: JSX.Element;
  onClickEvent: () => Promise<void> | void;
};

const identifyToBugsnag = ({ userId, email, name }: IdentifyArgs) => {
  if (!shouldEnableInsights()) {
    return;
  }
  Bugsnag.setUser(userId, email, name);
};

const Sidebar = ({ isOpen, onToggle }: SidebarProps) => {
  const discoveredOrganizations = useDiscoveredOrganizations();

  const navigate = useNavigate();

  const loggedInUser = useLoggedInMember();
  const loggedInOrganization = useLoggedInOrganization();
  useEffect(() => {
    if (!discoveredOrganizations?.length) {
      void getDiscoveredOrganizations();
    } if (discoveredOrganizations && discoveredOrganizations?.length > 1) {
      addSwitchOrgToMenu();
    }

  }, []);

  const addSwitchOrgToMenu = () => {
    setMenuItems([{ title: 'Switch Organization', icon: <OrganizationIcon />, onClickEvent: handleOrgSelect },
    ...menuItems
    ])

  }

  const {
    setDiscoveredOrganizations
  } = useLoginActions();

  const { logout } = useAuthActions();
  const sidebarRef = useRef<HTMLDivElement | null>(null);



  const handleLogout = async () => {
    const [error, resp] = await parsePromise(revokeToken());
    if (error) {
      addNotification({
        type: "error",
        title: "Error",
        message: error?.message,
      });
    }
    else {
      logout();
      authChannel.postMessage("loggedOut")
    }
  }

  const [menuItems, setMenuItems] = useState<MenuItem[]>([{ title: 'Logout', icon: <LogoutIcon />, onClickEvent: handleLogout }]);

  identifyToBugsnag({
    userId: loggedInUser?.member_id ?? "",
    email: loggedInUser?.email_address ?? "",
    name: loggedInUser?.name ?? "",
  });
  const handleOrgSelect = () => {
    setLoginStep(LOGIN_STEP.SELECT_ORG);
    navigate(`/${ROUTES.SELECT_ORG}`);
  };

  const getDiscoveredOrganizations = async () => {
    const [error, organizations] = await parsePromise(getOrganizationList());
    if (error) {
      addNotification({
        type: "error",
        title: "Error",
        message: error?.message,
      });
    } else {
      if (organizations?.discovered_organizations?.length > 1) {
        setDiscoveredOrganizations(organizations.discovered_organizations);
        addSwitchOrgToMenu();
      }
    }
    
  }



  return (
    <motion.div
      className="relative bg-surface-default h-full flex flex-col items-center justify-between"
      ref={sidebarRef}
      initial={isOpen ? "open" : "closed"}
      animate={isOpen ? "open" : "closed"}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      {/* Logo section */}
      <div className="flex-1 w-full flex flex-col">
        <div
          className={cn(
            "py-6 px-7 flex items-center gap-5 h-logo-area",
            !isOpen && "justify-center px-6",
          )}
        >
          {!isOpen ? (
            <ZaniaIcon className="w-5 h-5" />
          ) : (
            <ZaniaExpandedIcon className="h-6" />
          )}
        </div>
        <NavigationMenu isCollapsed={!isOpen} onExpandSidebar={onToggle} />
      </div>

      {/* User profile section */}
      <div className="w-full px-4 py-3">
        <div
          className={cn(
            "flex items-center justify-between rounded-lg py-2 px-3",
            isOpen ? "gap-3" : "justify-center",
          )}
        >
          {isOpen ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <ZAvatarGradient
                name={loggedInUser?.name ?? ""}
              />
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-text-primary font-medium text-sm truncate">
                    {loggedInUser?.name}
                  </p>
                  <Dropdown>
                    <DropdownTrigger>
                      <Button
                        isIconOnly
                        size="sm"
                        className="bg-transparent min-w-4 w-4 h-4 opacity-50 hover:opacity-100"
                      >
                        <Ellipsis className="h-4 w-4" color="#09090B" />
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu>
                      {menuItems.map((menuItem) => (
                        <DropdownItem
                          textValue=""
                          onClick={() => void menuItem.onClickEvent()}
                          key={menuItem.title}
                          className="px-0.5 py-0.5"
                        >
                          <div className="flex items-center gap-x-2.5 px-1 py-1">
                            {menuItem.icon}
                            <p>{menuItem.title}</p>
                          </div>
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </div>
                <ZTooltip
                  isDisabled={
                    !loggedInOrganization?.organization_name ||
                    loggedInOrganization.organization_name.length < 14
                  }
                  content={loggedInOrganization?.organization_name}
                  showArrow
                  placement="bottom"
                >
                  <p className="text-text-tertiary text-xs truncate">
                    {loggedInOrganization?.organization_name}
                  </p>
                </ZTooltip>
              </div>
            </div>
          ) : (
            <Dropdown>
              <DropdownTrigger>
                <Button
                  isIconOnly
                  className="bg-transparent"
                >
                  <ZAvatarGradient
                    name={loggedInUser?.name ?? ""}
                  />
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                {menuItems.map((menuItem) => (
                  <DropdownItem
                    textValue=""
                    onClick={() => void menuItem.onClickEvent()}
                    key={menuItem.title}
                    className="px-0.5 py-0.5"
                  >
                    <div className="flex items-center gap-x-2.5 px-1 py-1">
                      {menuItem.icon}
                      <p>{menuItem.title}</p>
                    </div>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
      </div>

      {/* Toggle button */}
      <span
        className="w-5 z-50 h-5 flex items-center justify-center bg-surface-default border border-border-default rounded-lg absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-1/2 cursor-pointer"
        onClick={onToggle}
      >
        <DoubleArrowIcon
          className={cn(!isOpen && "rotate-180")}
          stroke="#7A788E"
        />
      </span>
    </motion.div>
  );
};

export default Sidebar;
